import { useEffect, useRef } from "react";

/**
 * Triggers `callback` whenever the window/tab gains focus or becomes visible
 * again (but NOT on the initial mount — callers already fetch on mount).
 *
 * Fires on both:
 *  - `focus` (user alt-tabs back to this tab/window)
 *  - `visibilitychange → visible` (browser tab switches back)
 *
 * De-duplicates events that fire within 500ms of each other.
 */
export function useOnFocus(callback) {
    const cbRef = useRef(callback);
    cbRef.current = callback;
    const lastRunRef = useRef(0);
    const mountedAtRef = useRef(Date.now());

    useEffect(() => {
        const fire = () => {
            // Ignore the synthetic focus that fires on initial mount
            if (Date.now() - mountedAtRef.current < 500) return;
            const now = Date.now();
            if (now - lastRunRef.current < 500) return;
            lastRunRef.current = now;
            cbRef.current?.();
        };
        const onVisibility = () => {
            if (document.visibilityState === "visible") fire();
        };
        window.addEventListener("focus", fire);
        document.addEventListener("visibilitychange", onVisibility);
        return () => {
            window.removeEventListener("focus", fire);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, []);
}
